import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Banner,
  Button,
  Card,
  Chip,
  Field,
  Icon,
  Input,
  MoneyInput,
  RoleChip,
  Textarea,
  Toggle,
  useToast,
} from '@w7/ui'
import { cmApi } from '@/api/cmApi'
import { useCmMe } from '@/api/hooks/useCmMe'
import { isStaffCaller } from '@/app/staffAccess'
import { authHeaders } from '@/auth/session'
import {
  ROLE_LABEL,
  bpToCasinoSharePoints,
  bpToCommissionPercent,
  bpToSharePoints,
  casinoShareStep,
  commissionPercentToBp,
  canPickCurrency,
  creatableRoles,
  ASSIGNABLE_CURRENCY_TYPES,
  currencyLabel,
  displayToPaise,
  formatCasinoSharePoints,
  formatMoney,
  paiseToDisplay,
  isMemberRole,
  levelToRole,
  maxCasinoShareOf,
  maxShareOf,
  minCasinoShareOf,
  minShareOf,
  formatSharePoints,
  myShareKeep,
  shareStep,
  parseCurrencyType,
  shareError,
  sharePayloadBp,
  casinoCurrencyCreateFields,
  casinoCurrencyOptionsForSportsbook,
  resolveCasinoCurrencyForSportsbook,
  fundistCurrencyCreateFields,
  FUNDIST_CURRENCY_TYPES,
  parseFundistCurrencyType,
  siteUrlError,
  BUSINESS_TYPES,
  B2C_CATEGORIES,
  B2C_CATEGORY_LABEL,
  b2cCategoryError,
  b2cCreateFields,
  parseBusinessType,
  parseB2cCategory,
  type BusinessType,
  type B2cCategory,
  type CurrencyType,
  type FundistCurrencyType,
  type Role,
} from './hierarchy'
import {
  confirmPasswordError,
  isPasswordValid,
  passwordChecks,
  passwordError,
} from './passwordPolicy'
import { PasswordInput } from './PasswordInput'
import './create.css'

const GAME_KEYS = [
  'casino',
  'cardGame',
  'liveGame',
  'sportsbook',
  'premiumSportsbook',
] as const

const GAME_LABEL: Record<(typeof GAME_KEYS)[number], string> = {
  casino: 'Casino',
  cardGame: 'Card game',
  liveGame: 'Live game',
  sportsbook: 'Sportsbook',
  premiumSportsbook: 'Premium sportsbook',
}

type FormState = {
  role: Role | null
  username: string
  displayName: string
  siteUrl: string
  /** CM→TM B2B / B2C channel. */
  businessType: BusinessType
  /** Required when businessType is B2C. */
  b2cCategory: B2cCategory | ''
  password: string
  confirmPassword: string
  comments: string
  creditLimit: string
  shareSportsbook: string
  shareCasino: string
  fancy: string
  bookmaker: string
  matchOdds: string
  minBet: string
  maxBet: string
  currencyType: CurrencyType
  /** Lab casinoCurrencyType — Evolution HKD|AED (CM→TM). */
  casinoCurrencyType: 'HKD' | 'AED'
  /** Lab fundistCurrencyType — SoftGamings Crash Games (CM→TM). */
  fundistCurrencyType: FundistCurrencyType
  games: Record<(typeof GAME_KEYS)[number], boolean>
  /** Owner CM — force Keycloak CONFIGURE_TOTP on first agent-console login. */
  requireConsole2fa: boolean
}

const INITIAL: FormState = {
  role: 'triple-master',
  username: '',
  displayName: '',
  siteUrl: '',
  businessType: 'B2B',
  b2cCategory: '',
  password: '',
  confirmPassword: '',
  comments: '',
  creditLimit: '',
  shareSportsbook: '0',
  shareCasino: '0',
  fancy: '0',
  bookmaker: '0',
  matchOdds: '0',
  minBet: '100',
  maxBet: '',
  currencyType: 'INR',
  casinoCurrencyType: 'HKD' as 'HKD' | 'AED',
  fundistCurrencyType: 'HKD' as FundistCurrencyType,
  games: {
    casino: true,
    cardGame: true,
    liveGame: true,
    sportsbook: true,
    premiumSportsbook: false,
  },
  requireConsole2fa: true,
}

function parseNumber(raw: string): number | null {
  const n = Number(String(raw).replace(/,/g, '').trim())
  return Number.isFinite(n) ? n : null
}

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="w7-create-section" aria-labelledby={`${id}-h`}>
      <h2 id={`${id}-h`} className="w7-create-h2">
        {title}
      </h2>
      {children}
    </section>
  )
}

function SplitBar({
  label,
  segments,
}: {
  label: string
  segments: { id: string; value: number; color: string }[]
}) {
  const total = segments.reduce((sum, seg) => sum + Math.max(0, seg.value), 0)
  return (
    <div className="w7-splitbar" role="img" aria-label={label}>
      {segments.map((seg) => {
        const width = total > 0 ? (Math.max(0, seg.value) / total) * 100 : 0
        return width > 0 ? (
          <span key={seg.id} style={{ width: `${width}%`, background: seg.color }} />
        ) : null
      })}
    </div>
  )
}

export function CreateDownlinePage() {
  const navigate = useNavigate()
  const toast = useToast()
  const qc = useQueryClient()
  const me = useCmMe()
  const ownerCm = Boolean(me.data) && !isStaffCaller(me.data?.caller)
  const [values, setValues] = useState<FormState>(INITIAL)
  const [submitted, setSubmitted] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const self = useQuery({
    queryKey: ['cm', 'hierarchy', 'self'],
    queryFn: () => cmApi.hierarchySelf(authHeaders()),
  })

  const parentRole = self.data ? levelToRole(self.data.node.level) : 'company-master'
  const roles = useMemo(() => creatableRoles(parentRole), [parentRole])

  useEffect(() => {
    if (!roles.length) return
    setValues((prev) =>
      prev.role && roles.includes(prev.role) ? prev : { ...prev, role: roles[0]! },
    )
  }, [roles])
  // Remaining is stored as INR paise; show/compare it in the child currency
  // being created (CM INR leftover vs POINTS TM credit).
  const available = paiseToDisplay(self.data?.availableCredit, values.currencyType)

  useEffect(() => {
    if (!self.data?.node) return
    const inherited = parseCurrencyType(self.data.node.currencyType)
    setValues((prev) =>
      prev.role === 'triple-master' ||
      prev.role === 'player' ||
      prev.currencyType === inherited
        ? prev
        : { ...prev, currencyType: inherited },
    )
  }, [self.data?.node, values.role])
  const parentShareSb = bpToSharePoints(self.data?.node.shareBp ?? 0, values.currencyType)
  const parentShareCasino = bpToCasinoSharePoints(
    self.data?.node.shareCasinoBp ?? self.data?.node.shareBp ?? 0,
  )
  const parentFancy = bpToCommissionPercent(self.data?.node.commissionBp ?? 0)
  const parentBm = bpToCommissionPercent(
    self.data?.node.commissionBookmakerBp ?? self.data?.node.commissionBp ?? 0,
  )
  const parentMo = bpToCommissionPercent(
    self.data?.node.commissionMatchOddsBp ?? self.data?.node.commissionBp ?? 0,
  )

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setValues((prev) => ({ ...prev, [key]: value }))

  const credit = parseNumber(values.creditLimit)
  const shareSb = parseNumber(values.shareSportsbook) ?? 0
  const shareCasino = parseNumber(values.shareCasino) ?? 0
  const fancy = parseNumber(values.fancy) ?? 0
  const bookmaker = parseNumber(values.bookmaker) ?? 0
  const matchOdds = parseNumber(values.matchOdds) ?? 0
  const allocating = credit ?? 0
  const remaining = Math.max(0, available - allocating)
  const overAllocated = credit != null && credit > available + 1e-9
  const isMember = isMemberRole(values.role)
  // INR members: SportsBook forced to 100%. POINTS members: Master enter My Share.
  const showShareForm = !isMember || values.currencyType !== 'INR'
  const maxShareSb = maxShareOf(parentShareSb, values.currencyType)
  const maxShareCasino = maxCasinoShareOf(values.currencyType)
  const minShareSb = minShareOf(parentShareSb, values.currencyType)
  const minShareCasino = minCasinoShareOf(values.currencyType)
  const shareInc = shareStep(values.currencyType)
  const casinoInc = casinoShareStep(values.currencyType)
  const sliderMaxSb = maxShareSb ?? Math.max(100, shareSb, minShareSb)

  const errors = useMemo(() => {
    const e: Record<string, string | null> = {}
    if (!values.role) e.role = 'Pick a hierarchy role'
    const u = values.username.trim()
    e.username = u.length < 8 ? 'Username must be at least 8 characters' : null
    e.siteUrl = siteUrlError(values.siteUrl)
    e.b2cCategory = b2cCategoryError(values.businessType, values.b2cCategory)
    e.password = passwordError(values.password)
    e.confirmPassword = confirmPasswordError(values.password, values.confirmPassword)
    e.creditLimit =
      credit == null || credit < 1
        ? 'Credit limit must be at least 1'
        : overAllocated
          ? `Exceeds remaining ${formatMoney(available, values.currencyType)}`
          : null
    if (showShareForm) {
      e.shareSportsbook = shareError(
        shareSb,
        parentShareSb,
        'sportsbook',
        values.currencyType,
      )
      e.shareCasino = shareError(
        shareCasino,
        parentShareCasino,
        'casino',
        values.currencyType,
      )
    }
    e.fancy = fancy > parentFancy + 1e-9 ? `Max ${parentFancy.toFixed(2)}%` : null
    e.bookmaker = bookmaker > parentBm + 1e-9 ? `Max ${parentBm.toFixed(2)}%` : null
    e.matchOdds = matchOdds > parentMo + 1e-9 ? `Max ${parentMo.toFixed(2)}%` : null
    if (isMember) {
      const minB = parseNumber(values.minBet)
      const maxB = parseNumber(values.maxBet)
      e.minBet = minB == null || minB < 0 ? 'Enter a minimum bet' : null
      e.maxBet =
        maxB == null || maxB <= 0
          ? 'Enter a maximum bet'
          : minB != null && maxB < minB
            ? 'Max must be ≥ min'
            : null
    }
    return e
  }, [
    values,
    credit,
    overAllocated,
    available,
    isMember,
    showShareForm,
    shareSb,
    shareCasino,
    parentShareSb,
    parentShareCasino,
    fancy,
    bookmaker,
    matchOdds,
    parentFancy,
    parentBm,
    parentMo,
  ])

  const firstError = Object.entries(errors).find(([, v]) => v)?.[0] ?? null
  const ready = !firstError

  const create = useMutation({
    mutationFn: async () => {
      setSubmitted(true)
      if (firstError) throw new Error(errors[firstError] || 'Fix the highlighted fields')
      const username = values.username.trim().toLowerCase()
      const created = await cmApi.hierarchyCreate(
        authHeaders(),
        {
          name: values.displayName.trim() || username,
          creditLimit: displayToPaise(credit!, values.currencyType),
          currencyType: values.currencyType,
          ...casinoCurrencyCreateFields(
            values.role,
            values.casinoCurrencyType,
            values.currencyType,
          ),
          ...fundistCurrencyCreateFields(values.role, values.fundistCurrencyType),
          ...sharePayloadBp({
            isMember,
            sportsbookPoints: shareSb,
            casinoPoints: shareCasino,
            currency: values.currencyType,
          }),
          commissionBp: commissionPercentToBp(fancy),
          commissionBookmakerBp: commissionPercentToBp(bookmaker),
          commissionMatchOddsBp: commissionPercentToBp(matchOdds),
          member: isMember,
          username,
          password: values.password,
          siteUrl: values.siteUrl.trim(),
          ...b2cCreateFields(values.businessType, values.b2cCategory),
          comments: values.comments.trim() || undefined,
          games: { ...values.games },
          ...(isMember
            ? {
                betMin: displayToPaise(parseNumber(values.minBet) ?? 0, values.currencyType),
                betMax: displayToPaise(parseNumber(values.maxBet) ?? 0, values.currencyType),
              }
            : {}),
        },
        crypto.randomUUID(),
      )
      let mfaWarning: string | null = null
      if (ownerCm && !isMember && values.requireConsole2fa && created.node?.id) {
        try {
          await cmApi.consoleMfaSet(authHeaders(), {
            agentId: created.node.id,
            action: 'require-setup',
            confirm: true,
          })
        } catch (err) {
          mfaWarning = err instanceof Error ? err.message : String(err)
        }
      }
      return { created, mfaWarning }
    },
    onSuccess: ({ created, mfaWarning }) => {
      toast.success(`Created ${created.node.name}`)
      if (mfaWarning) {
        toast.error(`Account created, but Console 2FA require-setup failed: ${mfaWarning}`)
      }
      void qc.invalidateQueries({ queryKey: ['cm', 'hierarchy'] })
      void qc.invalidateQueries({ queryKey: ['cm', 'console-mfa'] })
      navigate('/hierarchy')
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    create.mutate()
  }

  const showErr = (field: string) => (submitted ? errors[field] ?? null : null)

  const parentName = self.data?.node.name ?? 'you'

  return (
    <div className="cm-page">
      <div className="w7-row" style={{ justifyContent: 'space-between', marginBottom: 16, gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem' }}>Create Triple Master</h1>
          <p className="cm-hint" style={{ marginTop: 6 }}>
            Assign credit from your remaining line · INR · sportsbook & casino share · fancy / BM /
            match-odds · games persisted
          </p>
        </div>
        <Button type="button" variant="secondary" onClick={() => navigate('/hierarchy')}>
          Back to hierarchy
        </Button>
      </div>

      {self.isError ? (
        <Banner tone="error" title="Could not load your limits">
          {(self.error as Error).message}
        </Banner>
      ) : null}

      <form className="w7-create" onSubmit={onSubmit} data-testid="create-downline-form">
        <div className="w7-create-main">
          <Banner tone="info" title="What this creates">
            Remaining credit {formatMoney(available, values.currencyType)} · your{' '}
            {values.currencyType === 'INR' ? 'My share' : 'My share'}{' '}
            {formatSharePoints(parentShareSb, values.currencyType)}
            · casino {formatCasinoSharePoints(parentShareCasino, values.currencyType)} · fancy{' '}
            {parentFancy.toFixed(2)}% · BM {parentBm.toFixed(2)}% · MO {parentMo.toFixed(2)}%.
          </Banner>

          <Section id="role" title="Role & identity">
            <Card
              title="Level"
              subtitle={`You are ${ROLE_LABEL[parentRole]}. CM creates Triple Masters only.`}
            >
              <div className="w7-optiongrid">
                {roles.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className="w7-option"
                    aria-pressed={option === values.role}
                    onClick={() => set('role', option)}
                  >
                    <span className="w7-option-head">
                      <RoleChip role={option} label={ROLE_LABEL[option]} />
                      {option === values.role ? <Icon name="check" size={16} /> : null}
                    </span>
                    <span className="w7-option-note">
                      {option === 'player'
                        ? 'Member (bottom of the ladder). Places bets; no downline of their own.'
                        : `Next rung under you — not a skip-level.`}
                    </span>
                  </button>
                ))}
              </div>
              {showErr('role') ? <p className="cm-hint" role="alert">{showErr('role')}</p> : null}
            </Card>

            <Card title="Login">
              <div className="w7-grid w7-grid-2">
                <Field label="Username (min 8)" required error={showErr('username')}>
                  {({ id, describedBy, invalid }) => (
                    <Input
                      id={id}
                      aria-describedby={describedBy}
                      invalid={invalid}
                      value={values.username}
                      onChange={(e) => set('username', e.target.value)}
                      autoComplete="off"
                    />
                  )}
                </Field>
                <Field label="Display name">
                  {({ id, describedBy }) => (
                    <Input
                      id={id}
                      aria-describedby={describedBy}
                      value={values.displayName}
                      onChange={(e) => set('displayName', e.target.value)}
                    />
                  )}
                </Field>
                <Field
                  label="Password"
                  required
                  hint="They will use this password to sign in. Keep it strong and share it securely."
                  error={showErr('password')}
                >
                  {({ id, describedBy, invalid }) => {
                    const passwordOk = isPasswordValid(values.password)
                    const passwordLiveInvalid = Boolean(values.password) && !passwordOk
                    return (
                      <>
                        <PasswordInput
                          id={id}
                          describedBy={describedBy}
                          invalid={invalid || passwordLiveInvalid}
                          valid={Boolean(values.password) && passwordOk}
                          value={values.password}
                          onChange={(value) => set('password', value)}
                          autoComplete="new-password"
                          visible={showPassword}
                          onToggleVisible={() => setShowPassword((v) => !v)}
                        />
                        {values.password ? (
                          <ul className="w7-create-pwchecks" aria-label="Password requirements">
                            {passwordChecks(values.password).map((check) => (
                              <li key={check.key} data-passed={check.passed ? 'true' : 'false'}>
                                <Icon name={check.passed ? 'check' : 'close'} size={12} />
                                {check.label}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </>
                    )
                  }}
                </Field>
                <Field label="Confirm password" required error={showErr('confirmPassword')}>
                  {({ id, describedBy, invalid }) => (
                    <PasswordInput
                      id={id}
                      describedBy={describedBy}
                      invalid={invalid}
                      value={values.confirmPassword}
                      onChange={(value) => set('confirmPassword', value)}
                      autoComplete="new-password"
                      visible={showConfirmPassword}
                      onToggleVisible={() => setShowConfirmPassword((v) => !v)}
                    />
                  )}
                </Field>
                <Field
                  label="Site URL"
                  required
                  hint="White-label hostname for this TM (Lab siteUrl)."
                  error={showErr('siteUrl')}
                >
                  {({ id, describedBy, invalid }) => (
                    <Input
                      id={id}
                      aria-describedby={describedBy}
                      invalid={invalid}
                      value={values.siteUrl}
                      onChange={(e) => set('siteUrl', e.target.value)}
                      placeholder="rimora.org"
                      autoComplete="off"
                    />
                  )}
                </Field>
                <Field
                  label="Business type"
                  required
                  hint="B2B or B2C channel for this Triple Master."
                >
                  {({ id }) => (
                    <select
                      id={id}
                      className="w7-input"
                      data-testid="create-business-type"
                      value={values.businessType}
                      onChange={(e) => {
                        const next = parseBusinessType(e.target.value)
                        setValues((prev) => ({
                          ...prev,
                          businessType: next,
                          b2cCategory: next === 'B2C' ? prev.b2cCategory : '',
                        }))
                      }}
                    >
                      {BUSINESS_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
                {values.businessType === 'B2C' ? (
                  <Field
                    label="B2C category"
                    required
                    hint="How this B2C Triple Master funds players."
                    error={showErr('b2cCategory')}
                  >
                    {({ id, describedBy, invalid }) => (
                      <select
                        id={id}
                        className="w7-input"
                        data-testid="create-b2c-category"
                        aria-describedby={describedBy}
                        aria-invalid={invalid || undefined}
                        value={values.b2cCategory}
                        onChange={(e) =>
                          set('b2cCategory', parseB2cCategory(e.target.value) ?? '')
                        }
                      >
                        <option value="">Select…</option>
                        {B2C_CATEGORIES.map((c) => (
                          <option key={c} value={c}>
                            {B2C_CATEGORY_LABEL[c]}
                          </option>
                        ))}
                      </select>
                    )}
                  </Field>
                ) : null}
              </div>
              <div style={{ marginTop: 'var(--w7-s4)' }}>
                <Field label="Comments" hint="Free text kept on the account.">
                  {({ id, describedBy }) => (
                    <Textarea
                      id={id}
                      aria-describedby={describedBy}
                      placeholder="Optional"
                      value={values.comments}
                      onChange={(e) => set('comments', e.target.value)}
                    />
                  )}
                </Field>
              </div>
              {ownerCm && !isMember ? (
                <div className="w7-create-console-2fa" data-testid="create-console-2fa">
                  <Field
                    label="Console 2FA"
                    hint="Owner CM only — Keycloak agent-console login. Not player MFA or Game Lock Authenticator."
                  >
                    {() => (
                      <span className="w7-list-row" style={{ padding: 0 }}>
                        <span>
                          {values.requireConsole2fa
                            ? 'Require 2FA setup on first login'
                            : 'Do not force 2FA setup'}
                        </span>
                        <Toggle
                          checked={values.requireConsole2fa}
                          onChange={(checked) => set('requireConsole2fa', checked)}
                          label={
                            values.requireConsole2fa
                              ? 'Require 2FA setup on first login'
                              : 'Do not force 2FA setup'
                          }
                        />
                      </span>
                    )}
                  </Field>
                </div>
              ) : null}
            </Card>
          </Section>

          <Section id="credit" title="Credit">
            <Card
              title="Credit limit"
              subtitle="Amounts are in the account currency. POINTS: 1 point = ₹100."
            >
              {canPickCurrency(values.role) ? (
                <>
                <Field
                  label="Currency"
                  required
                  hint={
                    values.role === 'player'
                      ? 'INR or Points (1 point = ₹100). Direct member under CM — TM downlines inherit their TM.'
                      : 'INR or Points (1 point = ₹100). Entire TM downline (DM / SM / Master / member) uses this.'
                  }
                >
                  {({ id }) => (
                    <select
                      id={id}
                      className="w7-input"
                      value={values.currencyType}
                      onChange={(e) => {
                        const next = parseCurrencyType(e.target.value)
                        setValues((prev) => ({
                          ...prev,
                          currencyType: next,
                          casinoCurrencyType: resolveCasinoCurrencyForSportsbook(
                            next,
                            prev.casinoCurrencyType,
                          ),
                        }))
                      }}
                    >
                      {ASSIGNABLE_CURRENCY_TYPES.map((c) => (
                        <option key={c} value={c}>
                          {currencyLabel(c)}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
                <Field
                  label="Casino Currency Flag"
                  required
                  hint={
                    values.currencyType === 'INR'
                      ? 'INR trees always use Evolution HKD (AED not allowed).'
                      : 'Lab Evolution currency (HKD or AED) for Points trees.'
                  }
                >
                  {({ id }) => (
                    <select
                      id={id}
                      className="w7-input"
                      value={values.casinoCurrencyType}
                      disabled={values.currencyType === 'INR'}
                      onChange={(e) =>
                        set(
                          'casinoCurrencyType',
                          resolveCasinoCurrencyForSportsbook(
                            values.currencyType,
                            e.target.value,
                          ),
                        )
                      }
                    >
                      {casinoCurrencyOptionsForSportsbook(values.currencyType).map(
                        (c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ),
                      )}
                    </select>
                  )}
                </Field>
                <Field
                  label="Crash Games Currency"
                  required
                  hint="SoftGamings Fundist wallet currency (Lab fundistCurrencyType). Default HKD."
                >
                  {({ id }) => (
                    <select
                      id={id}
                      className="w7-input"
                      value={values.fundistCurrencyType}
                      onChange={(e) =>
                        set(
                          'fundistCurrencyType',
                          parseFundistCurrencyType(e.target.value),
                        )
                      }
                    >
                      {FUNDIST_CURRENCY_TYPES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
                </>
              ) : null}
              <Field
                label={`Credit limit for this ${values.role ? ROLE_LABEL[values.role] : 'account'} (${currencyLabel(values.currencyType)})`}
                required
                hint={`Up to ${formatMoney(available, values.currencyType)} remaining.`}
                error={showErr('creditLimit')}
                aside={<Chip size="sm">Max {formatMoney(available, values.currencyType)}</Chip>}
              >
                {({ id, describedBy, invalid }) => (
                  <MoneyInput
                    id={id}
                    aria-describedby={describedBy}
                    invalid={invalid}
                    value={values.creditLimit}
                    onChange={(e) => set('creditLimit', e.target.value)}
                  />
                )}
              </Field>
              <div style={{ marginTop: 'var(--w7-s5)' }}>
                <SplitBar
                  label="Credit assignment"
                  segments={[
                    {
                      id: 'alloc',
                      value: allocating,
                      color: overAllocated ? 'var(--w7-loss)' : 'var(--w7-orange)',
                    },
                    { id: 'rest', value: remaining, color: 'var(--w7-border-strong)' },
                  ]}
                />
                <p className="w7-card-sub" style={{ marginTop: 'var(--w7-s3)' }}>
                  Assigning {formatMoney(allocating, values.currencyType)} · still available{' '}
                  {formatMoney(remaining, values.currencyType)}.
                </p>
              </div>
            </Card>
          </Section>

          <Section id="rates" title="Rates & commission">
            {showShareForm ? (
              <Card
                title="Partnership share"
                subtitle={
                  values.currencyType === 'INR'
                    ? 'SportsBook: Member Rate 0–1 (≥ yours). Casino: Member Rate 0–100.'
                    : 'POINTS SportsBook: My share any integer % (CM=0, player=100%). Casino: any numeric.'
                }
              >
                <div className="w7-grid w7-grid-2">
                  <Field
                    label={values.currencyType === 'INR' ? 'Member Rate' : 'My share'}
                    required
                    hint={
                      values.currencyType === 'INR'
                        ? `My Share: ${formatSharePoints(
                            myShareKeep(shareSb, parentShareSb, values.currencyType),
                            values.currencyType,
                          )} · (Min: ${formatSharePoints(minShareSb, values.currencyType)}) (Max: ${formatSharePoints(maxShareSb ?? 1, values.currencyType)})`
                        : 'Own keep % of pot (any integer ≥ 0)'
                    }
                    error={showErr('shareSportsbook')}
                  >
                    {({ id, describedBy, invalid }) => (
                      <Input
                        id={id}
                        aria-describedby={describedBy}
                        invalid={invalid}
                        numeric
                        step={shareInc}
                        min={minShareSb}
                        max={sliderMaxSb}
                        value={values.shareSportsbook}
                        onChange={(e) => set('shareSportsbook', e.target.value)}
                      />
                    )}
                  </Field>
                  <Field
                    label={
                      values.currencyType === 'INR' ? 'Member Rate (Casino)' : 'My share (Casino)'
                    }
                    required
                    hint={
                      values.currencyType === 'INR'
                        ? `(Min: 0) (Max: 100)`
                        : 'Point currency — any numeric value'
                    }
                    error={showErr('shareCasino')}
                  >
                    {({ id, describedBy, invalid }) => (
                      <Input
                        id={id}
                        aria-describedby={describedBy}
                        invalid={invalid}
                        numeric
                        step={casinoInc}
                        min={minShareCasino}
                        max={maxShareCasino ?? undefined}
                        value={values.shareCasino}
                        onChange={(e) => set('shareCasino', e.target.value)}
                      />
                    )}
                  </Field>
                </div>
                <div className="w7-field" style={{ marginTop: 'var(--w7-s4)' }}>
                  <span className="w7-field-label">
                    {values.currencyType === 'INR' ? 'Member Rate slider' : 'My share slider'}
                  </span>
                  <input
                    className="w7-range"
                    type="range"
                    min={minShareSb}
                    max={sliderMaxSb}
                    step={shareInc}
                    value={Math.min(Math.max(minShareSb, shareSb), sliderMaxSb)}
                    aria-label={values.currencyType === 'INR' ? 'Member Rate' : 'My share'}
                    onChange={(e) => set('shareSportsbook', e.target.value)}
                  />
                </div>
              </Card>
            ) : (
              <Card
                title="Partnership share"
                subtitle="INR players are stored at SportsBook 100% (rate 1)."
              >
                <p className="w7-card-sub">
                  Hierarchy forces sportsbook share_bp to 10000 for INR members. Casino leaf 100.
                </p>
              </Card>
            )}

            <Card
              title="Commission"
              subtitle="Fancy, bookmaker, and match-odds are stored as separate fields (Lab parity)."
            >
              <div className="w7-grid w7-grid-3">
                <Field label="Fancy %" hint={`Up to ${parentFancy.toFixed(2)}%.`} error={showErr('fancy')}>
                  {({ id, describedBy, invalid }) => (
                    <Input
                      id={id}
                      aria-describedby={describedBy}
                      invalid={invalid}
                      numeric
                      suffix="%"
                      value={values.fancy}
                      onChange={(e) => set('fancy', e.target.value)}
                    />
                  )}
                </Field>
                <Field
                  label="Bookmaker %"
                  hint={`Up to ${parentBm.toFixed(2)}%.`}
                  error={showErr('bookmaker')}
                >
                  {({ id, describedBy, invalid }) => (
                    <Input
                      id={id}
                      aria-describedby={describedBy}
                      invalid={invalid}
                      numeric
                      suffix="%"
                      value={values.bookmaker}
                      onChange={(e) => set('bookmaker', e.target.value)}
                    />
                  )}
                </Field>
                <Field
                  label="Match odds %"
                  hint={`Up to ${parentMo.toFixed(2)}%.`}
                  error={showErr('matchOdds')}
                >
                  {({ id, describedBy, invalid }) => (
                    <Input
                      id={id}
                      aria-describedby={describedBy}
                      invalid={invalid}
                      numeric
                      suffix="%"
                      value={values.matchOdds}
                      onChange={(e) => set('matchOdds', e.target.value)}
                    />
                  )}
                </Field>
              </div>
            </Card>
          </Section>

          {isMember ? (
            <Section id="games" title="Games & bet limits">
              <Card title="Game flags" subtitle="Persisted on the member node — enables product access." flush>
                <ul className="w7-list">
                  {GAME_KEYS.map((key) => (
                    <li key={key}>
                      <span className="w7-list-row">
                        <span>{GAME_LABEL[key]}</span>
                        <Toggle
                          checked={values.games[key]}
                          label={GAME_LABEL[key]}
                          onChange={(checked) =>
                            set('games', { ...values.games, [key]: checked })
                          }
                        />
                      </span>
                    </li>
                  ))}
                </ul>
              </Card>
              <Card title="Bet limits" subtitle="Stored in paise on the hierarchy node.">
                <div className="w7-grid w7-grid-2">
                  <Field label="Minimum bet (₹)" required error={showErr('minBet')}>
                    {({ id, describedBy, invalid }) => (
                      <MoneyInput
                        id={id}
                        aria-describedby={describedBy}
                        invalid={invalid}
                        value={values.minBet}
                        onChange={(e) => set('minBet', e.target.value)}
                      />
                    )}
                  </Field>
                  <Field label="Maximum bet (₹)" required error={showErr('maxBet')}>
                    {({ id, describedBy, invalid }) => (
                      <MoneyInput
                        id={id}
                        aria-describedby={describedBy}
                        invalid={invalid}
                        value={values.maxBet}
                        onChange={(e) => set('maxBet', e.target.value)}
                      />
                    )}
                  </Field>
                </div>
              </Card>
            </Section>
          ) : (
            <Section id="games" title="Games">
              <Card
                title="Game flags"
                subtitle="Also stored for agents — downline product defaults."
                flush
              >
                <ul className="w7-list">
                  {GAME_KEYS.map((key) => (
                    <li key={key}>
                      <span className="w7-list-row">
                        <span>{GAME_LABEL[key]}</span>
                        <Toggle
                          checked={values.games[key]}
                          label={GAME_LABEL[key]}
                          onChange={(checked) =>
                            set('games', { ...values.games, [key]: checked })
                          }
                        />
                      </span>
                    </li>
                  ))}
                </ul>
              </Card>
            </Section>
          )}
        </div>

        <aside className="w7-create-rail" id="review">
          <Card
            title="Final review"
            className="w7-create-railcard"
            footer={
              <div className="w7-create-railfoot">
                <Button type="submit" variant="primary" busy={create.isPending || self.isLoading}>
                  Create {values.role ? ROLE_LABEL[values.role] : 'account'}
                </Button>
                {!ready && submitted && firstError ? (
                  <p className="w7-create-block">{errors[firstError]}</p>
                ) : null}
              </div>
            }
          >
            <div className="w7-create-review" data-ready={ready ? 'true' : 'false'}>
              <div className="w7-create-review-status">
                <span className="w7-create-review-status-icon">
                  <Icon name={ready ? 'check' : 'warning'} size={18} />
                </span>
                <div>
                  <p className="w7-create-review-kicker">Ready check</p>
                  <p className="w7-create-review-status-title">
                    {ready ? 'Ready to create' : 'Complete the form'}
                  </p>
                  <p className="w7-create-review-status-copy">
                    {ready
                      ? `Creates under ${parentName}.`
                      : 'Fix highlighted fields, then create.'}
                  </p>
                </div>
              </div>
              <dl className="w7-create-review-facts">
                <div>
                  <dt>Role</dt>
                  <dd>{values.role ? ROLE_LABEL[values.role] : '—'}</dd>
                </div>
                <div>
                  <dt>Username</dt>
                  <dd>{values.username.trim() || '—'}</dd>
                </div>
                <div>
                  <dt>Site URL</dt>
                  <dd>{values.siteUrl.trim() || '—'}</dd>
                </div>
                <div>
                  <dt>Business type</dt>
                  <dd>{values.businessType}</dd>
                </div>
                {values.businessType === 'B2C' ? (
                  <div>
                    <dt>B2C category</dt>
                    <dd>
                      {values.b2cCategory
                        ? B2C_CATEGORY_LABEL[values.b2cCategory]
                        : '—'}
                    </dd>
                  </div>
                ) : null}
                <div>
                  <dt>Credit</dt>
                  <dd>{credit != null ? formatMoney(credit, values.currencyType) : '—'}</dd>
                </div>
                {showShareForm ? (
                  <>
                    <div>
                      <dt>{values.currencyType === 'INR' ? 'Member Rate' : 'My share'}</dt>
                      <dd>{formatSharePoints(shareSb, values.currencyType)}</dd>
                    </div>
                    {values.currencyType === 'INR' ? (
                      <div>
                        <dt>My Share</dt>
                        <dd>
                          {formatSharePoints(
                            myShareKeep(shareSb, parentShareSb, values.currencyType),
                            values.currencyType,
                          )}
                        </dd>
                      </div>
                    ) : null}
                    <div>
                      <dt>
                        {values.currencyType === 'INR'
                          ? 'Member Rate (Casino)'
                          : 'My share (Casino)'}
                      </dt>
                      <dd>{formatCasinoSharePoints(shareCasino, values.currencyType)}</dd>
                    </div>
                  </>
                ) : (
                  <div>
                    <dt>Member Rate</dt>
                    <dd>1 / Casino 100 (INR leaf)</dd>
                  </div>
                )}
                <div>
                  <dt>Fancy / BM / MO %</dt>
                  <dd>
                    {fancy.toFixed(2)} · {bookmaker.toFixed(2)} · {matchOdds.toFixed(2)}
                  </dd>
                </div>
              </dl>
            </div>
          </Card>
        </aside>

        <div className="w7-create-actionbar">
          <div className="w7-create-submit">
            <Button type="submit" variant="primary" busy={create.isPending || self.isLoading}>
              Create {values.role ? ROLE_LABEL[values.role] : 'account'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}
